/**
 * MSSQL built-in TYPE CONVERSION + CONDITIONAL/NULL-HANDLING function
 * signatures — one family slice of the static catalog composed by
 * `mssqlBuiltinRoutines.ts` (see that file's header for why this exists and
 * the precedence rule vs. user routines).
 *
 * `CAST`/`TRY_CAST` and `PARSE`/`TRY_PARSE` use the `expr AS type` special
 * syntax rather than comma-separated arguments — decision 5 (task brief)
 * calls these out as special forms that get best-effort labels rather than
 * fully mechanical comma-tracked parameters (there is no comma for
 * `countActiveParameter` to advance on, so the widget always highlights the
 * first "parameter"; the label is still a useful reminder of the shape).
 */

import type { RoutineMetadata, RoutineParameter } from '../../../../types/electron';

type Entry = RoutineMetadata & { documentation?: string };

function p(name: string, dataType: string): RoutineParameter {
  return { name, dataType };
}

function opt(name: string, dataType: string): RoutineParameter {
  return { name: `[${name}]`, dataType };
}

function variadic(dataType: string): RoutineParameter {
  return { name: '...', dataType };
}

function entry(name: string, parameters: RoutineParameter[], documentation?: string): Entry {
  return { schema: '', name, parameters, ...(documentation ? { documentation } : {}) };
}

export const MSSQL_CONVERSION_ROUTINES: ReadonlyMap<string, Entry> = new Map([
  // --- Type conversion / formatting (special `AS`/style syntax) ---
  ['cast', entry('CAST', [p('expression', 'any'), p('AS data_type', 'sql_type')], 'Convert an expression to another data type (ANSI syntax); errors on failure.')],
  ['convert', entry('CONVERT', [p('data_type', 'sql_type'), p('expression', 'any'), opt('style', 'int')], 'Convert an expression to another data type, with an optional format style.')],
  ['parse', entry('PARSE', [p('string_value', 'varchar'), p('AS data_type', 'sql_type'), opt('USING culture', 'varchar')], 'Parse a string into a date/time or numeric type, optionally with a culture.')],
  ['try_cast', entry('TRY_CAST', [p('expression', 'any'), p('AS data_type', 'sql_type')], 'Like CAST, but returns NULL on failure instead of raising an error.')],
  ['try_convert', entry('TRY_CONVERT', [p('data_type', 'sql_type'), p('expression', 'any'), opt('style', 'int')], 'Like CONVERT, but returns NULL on failure instead of raising an error.')],
  ['try_parse', entry('TRY_PARSE', [p('string_value', 'varchar'), p('AS data_type', 'sql_type'), opt('USING culture', 'varchar')], 'Like PARSE, but returns NULL on failure instead of raising an error.')],
  // --- Conditional / NULL-handling ---
  ['choose', entry('CHOOSE', [p('index', 'int'), p('val_1', 'any'), p('val_2', 'any'), variadic('any')], 'Return the item at the given 1-based index from a list of values.')],
  ['coalesce', entry('COALESCE', [p('expression1', 'any'), p('expression2', 'any'), variadic('any')], 'Return the first non-NULL expression among its arguments.')],
  ['iif', entry('IIF', [p('boolean_expression', 'bit'), p('true_value', 'any'), p('false_value', 'any')], 'Shorthand for a CASE expression: return true_value or false_value.')],
  ['isnull', entry('ISNULL', [p('check_expression', 'any'), p('replacement_value', 'any')], 'Return replacement_value if check_expression is NULL, else check_expression.')],
  ['nullif', entry('NULLIF', [p('expression1', 'any'), p('expression2', 'any')], 'Return NULL if the two expressions are equal, else the first expression.')],
]);
